import React, { memo, useState } from "react";
import { Handle, useReactFlow } from "react-flow-renderer";
import { useNodeStorage } from "../../../stores/useNodeStorage";

const TrashIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
    <path d="M9 3V4H4V6H5V20C5 21.1 5.9 22 7 22H17C18.1 22 19 21.1 19 20V6H20V4H15V3H9ZM7 6H17V20H7V6ZM9 8V18H11V8H9ZM13 8V18H15V8H13Z"/>
  </svg>
);
const DupeIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
  </svg>
);

const VARIABLES = [
  { label: "Produto",              value: "Produto" },
  { label: "Status de Pagamento",  value: "Status do Pagamento" },
  { label: "Evento",               value: "Evento" },
  { label: "Plataforma",           value: "Plataforma" },
  { label: "Valor da Compra",      value: "Valor da Compra" },
  { label: "País do Comprador",    value: "País do Comprador" },
];

const OPERATORS = [
  { label: "Contém",           value: "contains" },
  { label: "Igual a",          value: "eq" },
  { label: "Diferente de",     value: "neq" },
  { label: "Maior que",        value: "gt" },
  { label: "Menor que",        value: "lt" },
];

const STATUS_OPTIONS = ["Pago", "Pendente", "Cancelado", "Reembolsado", "Aprovado"];

const defaultCondition = () => ({ key: "Produto", condition: "contains", value: "" });

export default memo(({ data, isConnectable, id }) => {
  const storageItems = useNodeStorage();
  const { setNodes } = useReactFlow();

  // Support both old single-condition format and new multi-condition format
  const getConditions = () => {
    if (data.conditions && Array.isArray(data.conditions)) return data.conditions;
    if (data.key) return [{ key: data.key, condition: data.condition || "eq", value: data.value || "" }];
    return [defaultCondition()];
  };

  const conditions = getConditions();
  const matchAll = data.matchAll !== false; // default: all conditions

  const update = (patch) => {
    setNodes(old => old.map(n => n.id === id ? { ...n, data: { ...n.data, ...patch } } : n));
  };

  const setConditions = (newConds) => {
    update({ conditions: newConds, matchAll });
  };

  const updateCond = (index, field, val) => {
    const newConds = conditions.map((c, i) => i === index ? { ...c, [field]: val } : c);
    setConditions(newConds);
  };

  const addCond = () => setConditions([...conditions, defaultCondition()]);
  const removeCond = (index) => setConditions(conditions.filter((_, i) => i !== index));

  const inputStyle = {
    width: "100%", padding: "5px 8px", borderRadius: 7, fontSize: 11.5,
    border: "1px solid rgba(99,102,241,0.25)", background: "#fff",
    outline: "none", fontFamily: "Inter, sans-serif", color: "#0f172a",
    cursor: "pointer",
  };

  return (
    <div style={{
      background: "#ffffff",
      borderRadius: 14,
      border: "1.5px solid rgba(99,102,241,0.22)",
      boxShadow: "0 4px 16px rgba(99,102,241,0.08), 0 1px 4px rgba(0,0,0,0.04)",
      minWidth: 280, maxWidth: 320,
      fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
      position: "relative",
      overflow: "visible",
    }}>

      {/* Action buttons */}
      <div style={{ position: "absolute", top: -32, right: 0, display: "flex", gap: 6 }}>
        <button
          style={{ width: 26, height: 26, borderRadius: "50%", background: "#3b82f6", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff" }}
          title="Duplicar"
          onClick={() => { storageItems.setNodesStorage(id); storageItems.setAct("duplicate"); }}
        ><DupeIcon /></button>
        <button
          style={{ width: 26, height: 26, borderRadius: "50%", background: "#ef4444", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff" }}
          title="Excluir"
          onClick={() => { storageItems.setNodesStorage(id); storageItems.setAct("delete"); }}
        ><TrashIcon /></button>
      </div>

      {/* Input handle */}
      <Handle
        type="target"
        position="left"
        style={{ left: -7, top: 28, background: "#6366f1", width: 14, height: 14, border: "2px solid #fff", boxShadow: "0 2px 8px rgba(99,102,241,0.35)" }}
        isConnectable={isConnectable}
      />

      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "10px 12px 8px",
        borderBottom: "1px solid rgba(99,102,241,0.10)",
      }}>
        <div style={{
          width: 30, height: 30, borderRadius: 9,
          background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, boxShadow: "0 4px 10px rgba(99,102,241,0.28)",
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
          </svg>
        </div>
        <span style={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>Condição</span>
      </div>

      {/* Content */}
      <div style={{ padding: "10px 12px 12px", display: "flex", flexDirection: "column", gap: 8 }}>

        {/* Match mode toggle */}
        <div style={{ display: "flex", gap: 6, marginBottom: 2 }}>
          {[{ label: "Todas as condições", val: true }, { label: "Qualquer condição", val: false }].map(opt => (
            <button
              key={String(opt.val)}
              onMouseDown={e => e.stopPropagation()}
              onClick={() => update({ matchAll: opt.val, conditions })}
              style={{
                flex: 1, padding: "5px 4px", borderRadius: 8, fontSize: 10.5, fontWeight: 600,
                cursor: "pointer", border: "1.5px solid",
                borderColor: matchAll === opt.val ? "#6366f1" : "rgba(99,102,241,0.15)",
                background: matchAll === opt.val ? "rgba(99,102,241,0.08)" : "#f8fafc",
                color: matchAll === opt.val ? "#4f46e5" : "#94a3b8",
                transition: "all 0.15s",
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Condition rows */}
        {conditions.map((cond, idx) => (
          <div key={idx} style={{
            background: "rgba(99,102,241,0.04)", borderRadius: 10,
            border: "1px solid rgba(99,102,241,0.12)", padding: "8px 10px",
            display: "flex", flexDirection: "column", gap: 6,
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: "#6366f1", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {idx === 0 ? "SE" : (matchAll ? "E" : "OU")}
              </span>
              {conditions.length > 1 && (
                <button
                  onMouseDown={e => e.stopPropagation()}
                  onClick={() => removeCond(idx)}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", padding: 0, display: "flex", alignItems: "center" }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13H5v-2h14v2z"/></svg>
                </button>
              )}
            </div>

            {/* Variable */}
            <select
              value={cond.key}
              onChange={e => updateCond(idx, "key", e.target.value)}
              onMouseDown={e => e.stopPropagation()}
              style={inputStyle}
            >
              {VARIABLES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
            </select>

            {/* Operator */}
            <select
              value={cond.condition}
              onChange={e => updateCond(idx, "condition", e.target.value)}
              onMouseDown={e => e.stopPropagation()}
              style={inputStyle}
            >
              {OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>

            {/* Value — dropdown for Status, text input for others */}
            {cond.key === "Status do Pagamento" ? (
              <select
                value={cond.value}
                onChange={e => updateCond(idx, "value", e.target.value)}
                onMouseDown={e => e.stopPropagation()}
                style={inputStyle}
              >
                <option value="">Selecione...</option>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            ) : (
              <input
                type="text"
                value={cond.value}
                onChange={e => updateCond(idx, "value", e.target.value)}
                onMouseDown={e => e.stopPropagation()}
                placeholder={cond.key === "Produto" ? "Ex: 422 receitas..." : "Valor..."}
                style={{ ...inputStyle, cursor: "text" }}
              />
            )}
          </div>
        ))}

        {/* Add condition */}
        <button
          onMouseDown={e => e.stopPropagation()}
          onClick={addCond}
          style={{
            width: "100%", padding: "6px", borderRadius: 8, fontSize: 11.5, fontWeight: 600,
            cursor: "pointer", border: "1.5px dashed rgba(99,102,241,0.30)",
            background: "transparent", color: "#6366f1",
          }}
        >
          + Adicionar condição
        </button>

        {/* Output labels */}
        <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 2 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6, paddingRight: 4 }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: "#10b981" }}>✓ Atende a condição</span>
            <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#10b981", flexShrink: 0, boxShadow: "0 2px 5px #10b98155" }} />
            <Handle
              type="source" position="right" id="b"
              style={{ right: -7, width: 10, height: 10, background: "#10b981", border: "2px solid #fff", boxShadow: "0 2px 6px rgba(16,185,129,0.40)", position: "absolute" }}
              isConnectable={isConnectable}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6, paddingRight: 4, position: "relative" }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: "#ef4444" }}>✗ Não atende</span>
            <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#ef4444", flexShrink: 0, boxShadow: "0 2px 5px #ef444455" }} />
            <Handle
              type="source" position="right" id="a"
              style={{ right: -7, width: 10, height: 10, background: "#ef4444", border: "2px solid #fff", boxShadow: "0 2px 6px rgba(239,68,68,0.40)", position: "absolute" }}
              isConnectable={isConnectable}
            />
          </div>
        </div>
      </div>
    </div>
  );
});
